import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { baseAxios } from "../../utils/config";
import { UserServiceForm } from "../../components/user-service-form/user-service-form";

const UPDATE_USER_TO_SERVICE_API = "gateway/msyn4krb7c";
const FORM_ID = "edit-stylist-service-form";

export default function EditStylistServiceDialog({
  open,
  userId,
  serviceId,
  stylistName,
  onClose,
  onSaved,
}) {
  const formData = useRef({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [service, setService] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const busy = loading || fetching;

  useEffect(() => {
    if (!open || !userId || !serviceId) return;

    let cancelled = false;
    setFetching(true);
    setService(null);
    formData.current = {};

    const load = async () => {
      try {
        const res = await baseAxios.get(
          `services/${userId}/getServiceToEdit/${serviceId}`
        );
        if (cancelled) return;
        const next = res.data;
        setService(next);
        formData.current = {
          [next.service_id]: {
            priceLevel: next.price_level ? Number(next.price_level) : "",
            servicing: Number(next.service_step1_minutes) || "",
            gap: Number(next.service_step2_minutes) || "",
            servicing2: Number(next.service_step3_minutes) || "",
          },
        };
        setFormKey((k) => k + 1);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching service:", error);
          toast.error("Could not load service details.", { duration: 3000 });
          onClose?.();
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, userId, serviceId, onClose]);

  const handleClose = useCallback(() => {
    if (busy) return;
    onClose?.();
  }, [busy, onClose]);

  const handleSubmit = useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!service) return;

      const serviceData = formData.current[service.service_id] || {};
      const hasPrice =
        service.service_price_level_1 > 0 ||
        service.service_price_level_2 > 0 ||
        service.service_price_level_3 > 0 ||
        service.service_price_level_4 > 0 ||
        service.service_price_level_5 > 0;

      if (
        !serviceData.servicing ||
        serviceData.servicing < 15 ||
        (!serviceData.priceLevel && hasPrice)
      ) {
        toast.error(
          "Service need to have Price level and Servicing time set.",
          { duration: 3000 }
        );
        return;
      }

      setLoading(true);
      const payload = {
        userId,
        serviceId: service.service_id,
        priceLevel: serviceData.priceLevel || 1,
        servicing: serviceData.servicing,
        gap: serviceData.gap || 0,
        servicing2: serviceData.servicing2 || 0,
      };

      try {
        const response = await baseAxios.post(
          UPDATE_USER_TO_SERVICE_API,
          payload
        );
        if (response.status === 200) {
          toast.success("Service updated.", { duration: 3000 });
          onSaved?.();
          onClose?.();
        } else {
          toast.error("Error updating service.", { duration: 3000 });
        }
      } catch {
        toast.error("Error updating service.", { duration: 3000 });
      } finally {
        setLoading(false);
      }
    },
    [onClose, onSaved, service, userId]
  );

  const handleDelete = useCallback(async () => {
    if (!service) return;
    setLoading(true);
    try {
      const response = await baseAxios.post("services/deleteUserService", {
        serviceId: service.service_id,
        userId,
      });
      if (response.status === 200) {
        toast.success("Service removed.", { duration: 3000 });
        setConfirmDeleteOpen(false);
        onSaved?.();
        onClose?.();
      } else {
        toast.error("Error removing service.", { duration: 3000 });
      }
    } catch {
      toast.error("Error removing service.", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [onClose, onSaved, service, userId]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "secondary.main",
            color: "common.white",
            borderBottom: "1px solid",
            borderColor: "primary.main",
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="inherit">
              Edit service
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
            >
              {stylistName
                ? `Configure settings for ${stylistName}.`
                : "Configure stylist service settings."}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            disabled={busy}
            size="small"
            sx={{
              color: "primary.main",
              mt: 0.25,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {fetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} color="primary" />
            </Box>
          ) : service ? (
            <Box
              component="form"
              id={FORM_ID}
              onSubmit={handleSubmit}
              sx={{ p: 3 }}
            >
              <UserServiceForm
                key={formKey}
                service={service}
                loading={loading}
                formData={formData}
                deleteService={() => setConfirmDeleteOpen(true)}
              />
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            gap: 1,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={busy}
            variant="outlined"
            color="secondary"
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="contained"
            color="primary"
            disabled={busy || !service}
            sx={{
              minWidth: 140,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save service"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !busy && setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Remove service?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to remove{" "}
            <strong>{service?.service_name}</strong>
            {stylistName ? (
              <>
                {" "}
                for <strong>{stylistName}</strong>
              </>
            ) : null}
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={busy}
            variant="outlined"
            color="secondary"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={busy}
            color="error"
            variant="contained"
            size="small"
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
